import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { RbacService } from '../services/rbac.service';
import { AppModule } from '../rbac/rbac.policy';
import {
  homeRouteForOfficer,
  normalizeAppPath,
  portalFallbackUrlTree,
} from '../routing/portal-home';

/**
 * Use with route data: `{ module: AppModule }` or `{ roles: OfficerRole[] }`.
 * Prefer `module` so policy stays centralized in rbac.policy.ts.
 */
function denyToHome(
  auth: AuthService,
  rbac: RbacService,
  router: Router,
  stateUrl: string,
): ReturnType<CanActivateFn> {
  const home = homeRouteForOfficer(auth.officer());
  const current = normalizeAppPath(stateUrl);
  const homePath = normalizeAppPath(home);
  if (current === homePath) {
    return portalFallbackUrlTree((m) => rbac.canModule(m), router);
  }
  return router.createUrlTree([home]);
}

export const roleGuard: CanActivateFn = (route, state) => {
  const auth = inject(AuthService);
  const rbac = inject(RbacService);
  const router = inject(Router);

  // authGuard sits on the parent shell route, which the router reuses across
  // child navigations, so it never re-runs once the session is dropped. Without
  // this check a signed-out user falls through to the deny branch below and
  // lands on an unusable dashboard reporting `role (unknown)`.
  if (!auth.isAuthenticated()) {
    return router.createUrlTree(['/login'], { queryParams: { reason: 'session' } });
  }

  if (auth.needsRoleChoice()) {
    return router.createUrlTree(['/login']);
  }

  const module = route.data['module'] as AppModule | undefined;
  if (module) {
    if (rbac.canModule(module)) return true;
    return denyToHome(auth, rbac, router, state.url);
  }

  const roles = route.data['roles'] as string[] | undefined;
  if (roles?.length) {
    const role = rbac.role();
    if (role && roles.includes(role)) return true;
    return denyToHome(auth, rbac, router, state.url);
  }

  return true;
};
